import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { superAdminApi } from '../services/super-admin-api'

// Types for the API responses
export interface OrganizationListItem {
  id: string
  name: string
  slug: string
  subscription_status: string
  account_status: 'active' | 'suspended'
  trial_ends_at: string | null
  trial_days_remaining: number | null
  suspended_at?: string | null
  created_at: string
  plan?: string | null
  billing_email: string | null
  last_activity_at: string | null
  mrr?: number
  metrics: {
    active_members: number
    active_patients: number
    reports_30d: number
    engagement_score: number
  }
  alerts: {
    critical: number
    warning: number
  }
  subscription: {
    id?: string
    plan_name: string
    billing_interval: 'monthly' | 'yearly'
    current_period_end?: string
  } | null
}

interface PaginatedOrganizationsResponse {
  data: OrganizationListItem[]
  pagination: {
    page: number
    page_size: number
    total: number
    total_pages: number
  }
}

export interface OrganizationAdminAccess {
  email: string
  invite_sent: boolean
  temporary_password: string | null
  existing_user: boolean
}

export interface OrganizationCreateResult extends OrganizationListItem {
  admin_access?: OrganizationAdminAccess
}

interface UseOrganizationsParams {
  page?: number
  page_size?: number
  sort_by?: string
  sort_order?: 'asc' | 'desc'
  status?: string
  search?: string
  engagement_min?: number
  engagement_max?: number
  trial_expiring_days?: number
}

interface CreateOrganizationData {
  name: string
  slug?: string
  plan_id?: string
  billing_email?: string
  billing_name?: string
  trial_days?: number
  skip_trial?: boolean
  admin_email?: string
  admin_name?: string
  send_invite?: boolean
}

interface UpdateOrganizationData {
  name?: string
  billing_email?: string
  billing_name?: string
}

interface SuspendOrganizationData {
  reason?: string
  notify_admins?: boolean
}

interface ReactivateOrganizationData {
  reason?: string
  notify_admins?: boolean
}

interface DeleteOrganizationData {
  confirm_name: string
  reason?: string
}

export function useOrganizations(params: UseOrganizationsParams = {}) {
  const {
    page = 1,
    page_size = 20,
    sort_by = 'created_at',
    sort_order = 'desc',
    status,
    search,
    engagement_min,
    engagement_max,
    trial_expiring_days,
  } = params

  return useQuery<PaginatedOrganizationsResponse>({
    queryKey: ['super-admin', 'organizations', params],
    queryFn: async () => {
      return superAdminApi.get<PaginatedOrganizationsResponse>('/organizations', {
        page,
        page_size,
        sort_by,
        sort_order,
        status,
        search,
        engagement_min,
        engagement_max,
        trial_expiring_days,
      })
    },
    staleTime: 1000 * 60 * 2, // 2 minutes
  })
}

export function useCreateOrganization() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateOrganizationData) => {
      return superAdminApi.post<OrganizationCreateResult>('/organizations', data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['super-admin', 'organizations'] })
      queryClient.invalidateQueries({ queryKey: ['super-admin', 'dashboard'] })
    },
  })
}

export function useUpdateOrganization() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateOrganizationData }) => {
      return superAdminApi.patch<OrganizationListItem>(`/organizations/${id}`, data)
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['super-admin', 'organizations'] })
      queryClient.invalidateQueries({ queryKey: ['super-admin', 'organization', variables.id] })
    },
  })
}

export function useSuspendOrganization() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: SuspendOrganizationData }) => {
      return superAdminApi.post<OrganizationListItem>(`/organizations/${id}/suspend`, data)
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['super-admin', 'organizations'] })
      queryClient.invalidateQueries({ queryKey: ['super-admin', 'organization', variables.id] })
      queryClient.invalidateQueries({ queryKey: ['super-admin', 'dashboard'] })
    },
  })
}

export function useReactivateOrganization() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: ReactivateOrganizationData }) => {
      return superAdminApi.post<OrganizationListItem>(`/organizations/${id}/reactivate`, data)
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['super-admin', 'organizations'] })
      queryClient.invalidateQueries({ queryKey: ['super-admin', 'organization', variables.id] })
      queryClient.invalidateQueries({ queryKey: ['super-admin', 'dashboard'] })
    },
  })
}

export function useDeleteOrganization() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: DeleteOrganizationData }) => {
      return superAdminApi.delete<{ success: true; id: string; name: string }, DeleteOrganizationData>(
        `/organizations/${id}`,
        data
      )
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['super-admin', 'organizations'] })
      queryClient.removeQueries({ queryKey: ['super-admin', 'organization', variables.id] })
      queryClient.invalidateQueries({ queryKey: ['super-admin', 'dashboard'] })
    },
  })
}
